import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { catchError, EMPTY, finalize, from, map, mergeMap, Observable, of, switchMap, tap, toArray } from 'rxjs';

import { IdValueType } from '@models/common';
import { GridFormComponent } from '@module-definition/form/grid-form/grid-form.component';
import { DmFormField } from '@module-instance/dynamic-module/common';
import { ModuleInstanceService } from '@module-instance/services';
import { CipoFieldConfig, CipoFieldTypes, mapFieldErrors } from '@shared/components/fields/common';
import { NotificationService } from '@shared/services';
import { DictionaryStore } from '@signal-store/index';
import { CompactModeLevel, SubmittableForm } from '../common';

@Component({
  selector: 'cipo-dynamic-form',
  imports: [GridFormComponent, NgxSkeletonLoaderModule],
  templateUrl: './dynamic-form.component.html',
})
export class DynamicFormComponent implements SubmittableForm {
  private destroyRef = inject(DestroyRef);
  private moduleInstanceService = inject(ModuleInstanceService);
  private dictionaryStore = inject(DictionaryStore);
  private notify = inject(NotificationService);

  contractId = input.required<number>();
  form = input.required<FormGroup>();
  fields = input.required<DmFormField[]>();
  /* TODO: implement this functionality. Compact mode should be controlled by horizontal splitter's position & screen size. 
  Maybe implement it alongside mobile view form definition */
  compactMode = input<CompactModeLevel>();
  readonly = input<boolean>(true);
  formValuesPrepared = output<IdValueType<any, number>[]>();

  keysOrdered = computed(() => {
    const fields = this.fields();
    if (!fields) {
      return [];
    }
    return fields.map(f => f.id);
  });
  fieldsWithTriggers = computed(() => this.fields().filter(f => f.triggeredFields?.length));
  reverseTriggers = computed<Record<number, number[]>>(() => {
    const result: Record<number, number[]> = {};

    for (const field of this.fields()) {
      if (!field.triggeredFields) continue;

      for (const triggeredId of field.triggeredFields) {
        if (!result[triggeredId]) {
          result[triggeredId] = [];
        }
        result[triggeredId].push(Number(field.id));
      }
    }

    return result;
  });
  submitting = signal(false);
  /** FieldsData dict. This is the fieldsData populated on gridFields change */
  originalFieldsData = computed(() => {
    const fields = this.fields();
    if (!fields) {
      return {};
    }
    const contractId = untracked(this.contractId);
    const data: Record<string, CipoFieldConfig> = {};
    fields.forEach(f => {
      data[f.id] = {
        ...f.fieldOptions,
        markChanged: true,
        contractId,
      };
    });
    return data;
  });
  mappedFieldData = linkedSignal(() => {
    let fieldsData = this.originalFieldsData();
    if (!Object.keys(fieldsData).length) {
      return null;
    }
    untracked(() => {
      const compactMode = this.compactMode();
      if (compactMode) {
        fieldsData = this.mapFieldsCompactMode(fieldsData, compactMode);
      }
      if (this.readonly()) {
        fieldsData = this.mapFieldsReadonly(fieldsData);
      }
    });
    return fieldsData;
  });
  calculatingFormula = false;

  constructor() {
    effect(() => {
      const fields = this.fields();
      if (fields) {
        untracked(() => this.initForm([...fields]));
      }
    });

    effect(() =>
      this.mappedFieldData.update(fieldsData => (fieldsData ? this.mapFieldsReadonly(fieldsData) : fieldsData)),
    );

    effect(() =>
      this.mappedFieldData.update(fieldsData =>
        fieldsData ? this.mapFieldsCompactMode(fieldsData, this.compactMode()) : fieldsData,
      ),
    );
  }

  getFormValue() {
    const formValue = this.form().getRawValue();
    return Object.entries(formValue)
      .map(([key, value]) => {
        /* Remove this after implementing form definition. Editor and multiline are (now) always single, but BE waits for an array when multiple: true */
        const field = this.fields().find(f => f.id === key);
        if (field?.fieldOptions.type === CipoFieldTypes.Annotation) {
          return null;
        }
        if (field?.fieldOptions.hasMultipleValues && !Array.isArray(formValue[key])) {
          return { id: Number(key), value: value ? [value] : [] };
        }
        return { id: Number(key), value: formValue[key] };
      })
      .filter(val => !!val);
  }

  /** This method is called from outside of the component. It's meant to check if there is no formula calculation in progress.
   *
   * If there is, the submit will be called again after the calculation is done.
   *
   * Else, it will emit the formValuesPrepared event.
   *
   * This can be skipped if the form doesn't have formula fields.
   */
  submit() {
    this.submitting.set(true);
    if (this.calculatingFormula) {
      return;
    }
    this.formValuesPrepared.emit(this.getFormValue());
  }

  initForm(fields: DmFormField[]) {
    /* If there is at least one RichText field, fetch tag users in dictionary. Froala will know to read them */
    const froalaField = fields.find(f => f.fieldOptions.type === CipoFieldTypes.RichText);
    if (froalaField) {
      const { contractId, moduleId } = froalaField.fieldOptions;
      this.dictionaryStore.getTagUsers({ contractId, moduleId }).subscribe();
    }

    fields.forEach(({ triggeredFields, ...field }) => {
      const { id, value } = structuredClone(field);
      /* nonNullable: true allows to reset a form to it's initial value. All the fields that are nullable are reset to null */
      const control = new FormControl(value, { nonNullable: !!value });
      if (
        field.fieldOptions.dataSourceInfo?.parents?.length ||
        field.fieldOptions.dataSourceInfo?.childFieldIds?.length
      ) {
        this.handleRelatedListControl(control, Number(id));
      }
      this.form().addControl(id, control, { emitEvent: false });
    });
  }

  checkFormulaField(key: string) {
    if (!this.fieldsWithTriggers().some(f => f.id === key)) {
      return;
    }
    this.calculatingFormula = true;
    this.recalcTriggeredFields(Number(key), this.form().getRawValue()).subscribe(() => {
      this.calculatingFormula = false;
      /* If the form was submitting while calculating formulas - submit now */
      if (this.submitting()) {
        this.formValuesPrepared.emit(this.getFormValue());
      }
    });
  }

  private recalcTriggeredFields(fieldId: number, formValue: any): Observable<void> {
    /* Check if this field has triggeredFields */
    const triggeredFields = this.fields().find(f => Number(f.id) === fieldId)?.triggeredFields ?? [];
    if (!triggeredFields.length) return of(void 0);

    triggeredFields.forEach(tf => this.form().controls[tf].disable());

    const body: IdValueType<unknown>[] = Object.entries(formValue).map(([id, value]) => ({
      id: Number(id),
      value,
    }));

    return from(triggeredFields).pipe(
      /* Call getFormulaFieldValue for each triggered field */
      mergeMap(tf =>
        this.moduleInstanceService.getFormulaFieldValue({ fieldId: tf, body, contractId: this.contractId() }).pipe(
          tap(({ value }) => {
            const newVal = value ?? null;
            this.form().controls[tf].setValue(newVal);
          }),
          /* This calls the recursive method with the triggered field (for nested formulas) */
          switchMap(() => this.recalcTriggeredFields(tf, this.form().getRawValue())),
          catchError((err: HttpErrorResponse) => {
            // Enabling and resetting value recalculate validity, so do both before setting manual backend errors.
            this.form().controls[tf].enable({ emitEvent: false });
            this.form().controls[tf].setValue(null);
            const errors = mapFieldErrors(err.error, this.fields());
            let handledOnField = false;
            errors?.forEach(({ key, message }) => {
              const control = this.form().controls[key];
              if (control) {
                control.setErrors({ custom: message, informative: true });
                handledOnField = true;
              }
            });
            if (!handledOnField) {
              this.notify.error(err);
            }
            return EMPTY;
          }),
          finalize(() => {
            const control = this.form().controls[tf];
            // The error handler already enabled the control; enabling again would clear its errors.
            if (control.disabled) {
              control.enable({ emitEvent: false });
            }
          }),
        ),
      ),
      toArray(), // wait for all recursions to complete
      map(() => void 0),
    );
  }

  private mapFieldsReadonly(fieldsData: Record<string, CipoFieldConfig>) {
    const updated = { ...fieldsData };
    Object.keys(updated).forEach(key => {
      updated[key] = {
        ...updated[key],
        readonly: this.getReadonlyState(Number(key), updated[key].dataSourceInfo?.parents),
      };
    });
    return updated;
  }

  private getReadonlyState(fieldId: number, parents = this.originalFieldsData()[fieldId].dataSourceInfo?.parents) {
    const fieldData = this.originalFieldsData()[fieldId];
    /* Return 'true' if the field is readonly by definition or the form is view-only mode */
    if (fieldData.readonly || this.readonly()) {
      return true;
    }
    if (!parents?.length) {
      return false;
    }
    /* If at least one parent doesn't have any value selected - mark (this child) as readonly */
    return parents.some(parent => !parent.parentElementIds.length);
  }

  private handleRelatedListControl(control: FormControl, fieldId?: number) {
    const { childFieldIds, parents } = this.originalFieldsData()[fieldId].dataSourceInfo;
    /* If at least one parent doesn't have any value selected - mark (this child) as readonly */
    if (parents?.some(parent => !parent.parentElementIds.length)) {
      this.mappedFieldData.update(fd => ({
        ...fd,
        [fieldId]: {
          ...fd[fieldId],
          readonly: true,
        },
      }));
    }
    control.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((val: string | string[]) => (Array.isArray(val) ? val : val ? [String(val)] : [])),
      )
      .subscribe(val => {
        /* Get all children ids for this parent field (on any level) */
        const allChildrenIds = this.fields()
          .filter(f => f.fieldOptions.dataSourceInfo?.parents?.some(p => p.parentFieldId === fieldId))
          .map(f => Number(f.id));

        /* Update child controls */
        allChildrenIds.forEach(childFieldId => {
          const childControl = this.form().controls[childFieldId];

          /* If the parent field changes, reset all their child controls */
          if (childControl.value !== null) {
            childControl.setValue(null);
          }

          const isDirectChild = childFieldIds?.includes(childFieldId);

          this.mappedFieldData.update(fd => {
            /* Update the parentElementIds */
            const currentParentIndex = fd[childFieldId].dataSourceInfo.parents.findIndex(
              p => p.parentFieldId === fieldId,
            );
            const updatedParents = [...fd[childFieldId].dataSourceInfo.parents];
            const newParentEntry = {
              parentFieldId: fieldId,
              parentElementIds: val,
            };
            if (currentParentIndex !== -1) {
              updatedParents[currentParentIndex] = newParentEntry;
            } else {
              updatedParents.push(newParentEntry);
            }
            return {
              ...fd,
              [childFieldId]: {
                ...fd[childFieldId],

                /* Keep configuration/view-only restrictions and disable fields with missing parents */
                readonly: !isDirectChild || this.getReadonlyState(childFieldId, updatedParents),
                options: isDirectChild && val.length ? null : [],

                dataSourceInfo: {
                  ...fd[childFieldId].dataSourceInfo,
                  parents: updatedParents,
                },
              },
            };
          });
        });
      });
  }

  private mapFieldsCompactMode(fieldsData: Record<string, CipoFieldConfig>, compactMode: CompactModeLevel) {
    /* TODO: Add implementation for compact mode */
    if (!compactMode) {
      return fieldsData;
    }
    const newFieldsData: Record<string, CipoFieldConfig> = {};
    let yPosition = 0;
    for (const key in fieldsData) {
      newFieldsData[key] = {
        ...fieldsData[key],
        position: {
          ...fieldsData[key].position,
          cols: 12,
          x: 0,
          y: yPosition,
        },
      };
      yPosition += fieldsData[key].position?.rows || 1;
    }
    return newFieldsData;
  }
}
